'use strict';

const HTMLCustomElement = require('@emartech/ui-framework-utils').HTMLCustomElement;
const Popper = require('popper.js');
const popperTemplate = require('./popper.pug');

class VcePositionerBase extends HTMLCustomElement {
  init() {
    this.placement = 'top';

    this._iframeDocument = null;
    this._targetElement = null;
    this._iframeElement = null;
    this._popper = null;
    this._popperElement = null;
    this._borderer = null;
    this._borderStyle = 'dashed';
    this._opened = false;
    this._scrollDelay = 100;
    this._scrollTimeout = null;
    this._zIndex = 500;
    this._flipped = false;

    this._events = {};
    this._events.mouseScroll = this._mouseScroll.bind(this);
  }

  connectedCallback() {
    window.addEventListener('unload', this.close.bind(this));
    this._resetStyle(this);
  }

  disconnectedCallback() {
    this.close();
  }

  static get observedAttributes() {
    return [];
  }

  open(targetElement, iframe) {
    this.close();

    this._opened = true;
    this._setupElements(targetElement, iframe);

    this._popper = this._createPopper();
    this._createBorderer();
    this._scrollEvent('add');
  }

  close() {
    this._close(popperContent => {
      this.appendChild(popperContent);
    });
  }

  _close(popperHandler) {
    if (!this._opened) {
      return;
    }
    this._opened = false;

    const popperContent = this._prepareContent();
    popperHandler(popperContent);

    this._destroyPopper();
    this._removeBorderer();
    this._scrollEvent('remove');
  }

  _scrollEvent(action) {
    const eventListenerAction = action + 'EventListener';

    if (this._isDocumentsSame()) {
      const parentScrollElements = this._iframeElement
        .ownerDocument.querySelectorAll('.positioner-recalculate-container');
      [].forEach.call(
        parentScrollElements, element => element[eventListenerAction]('scroll', this._events.mouseScroll)
      );
    } else {
      const scrollElements = document.querySelectorAll('.positioner-recalculate-container');
      [].forEach.call(scrollElements, element => element[eventListenerAction]('scroll', this._events.mouseScroll));
    }

    const elementWindow = this._iframeDocument.defaultView;
    if (!elementWindow) {
      return;
    }

    elementWindow[eventListenerAction]('scroll', this._events.mouseScroll);
  }

  _mouseScroll() {
    if (this._iframeDocument.body.style.height === '101%') {
      return;
    }

    this._popperElement.style.visibility = 'hidden';

    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    this._scrollTimeout = setTimeout(() => {
      this._popperElement.style.visibility = 'visible';
      if (this._popper) {
        this._popper.update();
      }
    }, this._scrollDelay);
  }

  _setupElements(targetElement, iframe) {
    this._targetElement = targetElement;
    this._iframeElement = iframe;
    this._iframeDocument = iframe.contentDocument;
  }

  _createPopper() {
    const parentDocument = this._iframeElement.ownerDocument;
    this._popperElement = this._createPopperContainer();
    parentDocument.body.appendChild(this._popperElement);
    this._popperElement.appendChild(this.children[0]);
    this.appendChild(this._popperElement.children[0]);
    this._popperElement.appendChild(this.children[0]);
    this._resetStyle(this._popperElement);

    return new Popper(this._targetElement, this._popperElement, {
      placement: this.placement,
      removeOnDestroy: true,
      modifiers: {
        preventOverflow: {
          boundariesElement: 'viewport',
          padding: 0
        },
        applyIframeStyle: {
          enabled: true,
          function: data => this._calculatePositionInIframe(data),
          order: 800
        },
        applyStyle: {
          gpuAcceleration: false
        }
      }
    });
  }

  _createPopperContainer() {
    let popperElement = this.ownerDocument.createElement('div');
    popperElement.innerHTML = popperTemplate({
      'zIndex': this._zIndex
    });
    popperElement = popperElement.firstChild;

    return popperElement;
  }

  _prepareContent() {
    const content = this._popperElement.children[0];
    this._popperElement.removeChild(content);

    return content;
  }

  _resetStyle(element) {
    element.style.position = 'absolute';
    element.style.left = '-9999px';
    element.style.top = 0;
  }

  _calculatePositionInIframe(data) {
    this._flipped = data.flipped;
    const { top: iframeTop, left: iframeLeft } = this._iframeElement.getBoundingClientRect();
    const { height: iframeHeight, width: iframeWidth } = this._iframeElement.getBoundingClientRect();
    const { height: popperHeight, width: popperWidth } = this._popperElement.getBoundingClientRect();
    const leftBoundary = iframeLeft + iframeWidth - popperWidth;

    if (this._isDocumentsSame()) {
      const container = {
        top: window.pageYOffset || this.ownerDocument.documentElement.scrollTop,
        left: window.pageXOffset || this.ownerDocument.documentElement.scrollLeft
      };
      const scrollTop = top.document.documentElement.scrollTop;
      const scrollLeft = top.document.documentElement.scrollLeft;
      const topBoundary = scrollTop + iframeTop - popperHeight;
      data.offsets.popper.top += scrollTop + iframeTop - container.top;
      data.offsets.popper.left += scrollLeft + iframeLeft - container.left;

      if (data.offsets.popper.top < topBoundary) {
        data.offsets.popper.top = topBoundary;
      }
    } else {
      const bottomBoundary = iframeTop + iframeHeight - popperHeight;
      data.offsets.popper.top += iframeTop;
      data.offsets.popper.left += iframeLeft;

      if (data.offsets.popper.top > bottomBoundary) {
        data.offsets.popper.top = bottomBoundary;
      }
    }

    if (data.offsets.popper.left > leftBoundary) {
      data.offsets.popper.left = leftBoundary;
    }

    return data;
  }

  _createBorderer() {
    this._borderer = this._iframeDocument.createElement('e-vce-borderer');
    this._borderer.setAttribute('highlight', true);
    this._borderer.setAttribute('borderstyle', this._borderStyle);
    this._borderer.target = this._targetElement;
    this._iframeDocument.body.appendChild(this._borderer);
  }

  _removeBorderer() {
    if (!this._borderer) {
      return;
    }

    this._iframeDocument.body.removeChild(this._borderer);
  }


  _isDocumentsSame() {
    return this.ownerDocument === this._targetElement.ownerDocument;
  }

  _destroyPopper() {
    if (!this._popper) {
      return;
    }

    this._popper.destroy();
    this._popper = null;
  }
}

module.exports = VcePositionerBase;
